import { useState } from 'react';
import axios from 'axios';
import { useUser } from '../../context/UserContext';

const My_Account = () => {
	const [activeSection, setActiveSection] = useState('account');

	return (
		<div id="my-account" className="my-account">
			<h1>My Account</h1>
			<div className="my-account__layout">

				{/* ---- LEFT NAV (1/3) ---- */}
				<nav className="my-account__nav">
					<ul className="my-account__nav-list">
						<li>
							<button
								id="nav-account-details"
								className={`my-account__nav-btn ${activeSection === 'account' ? 'my-account__nav-btn--active' : ''}`}
								onClick={() => setActiveSection('account')}
							>
								Account details
							</button>
						</li>
						<li>
							<button
								id="nav-appearance"
								className={`my-account__nav-btn ${activeSection === 'appearance' ? 'my-account__nav-btn--active' : ''}`}
								onClick={() => setActiveSection('appearance')}
							>
								Appearance
							</button>
						</li>
					</ul>
				</nav>

				{/* ---- RIGHT CONTENT (2/3) ---- */}
				<section className="my-account__content">
					{activeSection === 'account' && <AccountDetails />}
					{activeSection === 'appearance' && <AppearanceSettings />}
				</section>

			</div>
		</div>
	);
}

/* ------------------------------------------------------------------ */
/*  AccountDetails                                                     */
/* ------------------------------------------------------------------ */

const AccountDetails = () => {
	const { user } = useUser();
	const isGoogleUser = user?.authProvider === 'google';

	// --- Email form state ---
	const [newEmail, setNewEmail] = useState('');
	const [emailPassword, setEmailPassword] = useState('');
	const [emailResult, setEmailResult] = useState(null);
	const [emailLoading, setEmailLoading] = useState(false);

	// --- Password form state ---
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [passwordResult, setPasswordResult] = useState(null);
	const [passwordLoading, setPasswordLoading] = useState(false);

	const handleUpdateEmail = async (e) => {
		e.preventDefault();
		setEmailResult(null);

		if (!newEmail) {
			setEmailResult({ success: false, message: 'Please enter a new email address.' });
			return;
		}

		setEmailLoading(true);
		try {
			const { data } = await axios.post(
				`${process.env.REACT_APP_WEB_URL}update-email`,
				{ newEmail, currentPassword: emailPassword },
				{ withCredentials: true }
			);
			setEmailResult({ success: data.success, message: data.message });
			if (data.success) {
				setNewEmail('');
				setEmailPassword('');
			}
		} catch (error) {
			setEmailResult({ success: false, message: 'Something went wrong. Please try again.' });
		} finally {
			setEmailLoading(false);
		}
	};

	const handleUpdatePassword = async (e) => {
		e.preventDefault();
		setPasswordResult(null);

		if (!newPassword) {
			setPasswordResult({ success: false, message: 'Please enter a new password.' });
			return;
		}
		if (newPassword !== confirmPassword) {
			setPasswordResult({ success: false, message: 'New passwords do not match.' });
			return;
		}

		setPasswordLoading(true);
		try {
			const { data } = await axios.post(
				`${process.env.REACT_APP_WEB_URL}update-password`,
				{ currentPassword, newPassword },
				{ withCredentials: true }
			);
			setPasswordResult({ success: data.success, message: data.message });
			if (data.success) {
				setCurrentPassword('');
				setNewPassword('');
				setConfirmPassword('');
			}
		} catch (error) {
			setPasswordResult({ success: false, message: 'Something went wrong. Please try again.' });
		} finally {
			setPasswordLoading(false);
		}
	};

	return (
		<div className="my-account__panel">

			{/* ---- Update email ---- */}
			{isGoogleUser ? (
				<div className="my-account__field-group">
					<h2>Update email address</h2>
					<p className="my-account__hint">
						You have signed up via your Google account. If you need to change your email address, please email our support at{' '}
						<a href="mailto:harryraywebdev@gmail.com">harryraywebdev@gmail.com</a>.
					</p>
				</div>
			) : (
				<form className="my-account__field-group" onSubmit={handleUpdateEmail}>
					<h2>Update email address</h2>
					<div className="form__group">
						<label htmlFor="email-input" className="form__label">New email address</label>
						<input
							id="email-input"
							type="email"
							className="form__input"
							placeholder="New email address"
							value={newEmail}
							onChange={(e) => setNewEmail(e.target.value)}
						/>
					</div>
					<div className="form__group">
						<label htmlFor="email-password-input" className="form__label">Current password</label>
						<input
							id="email-password-input"
							type="password"
							className="form__input"
							placeholder="Current password (to confirm)"
							value={emailPassword}
							onChange={(e) => setEmailPassword(e.target.value)}
						/>
					</div>
					<button
						id="update-email-btn"
						type="submit"
						className="form__button form__button--primary"
						disabled={emailLoading}
					>
						{emailLoading ? 'Updating…' : 'Update email'}
					</button>
					{emailResult && (
						<p className={`my-account__result ${emailResult.success ? 'my-account__result--success' : 'my-account__result--error'}`}>
							{emailResult.message}
						</p>
					)}
				</form>
			)}

			{/* ---- Update password ---- */}
			{isGoogleUser ? (
				<div className="my-account__field-group">
					<h2>Update password</h2>
					<p className="my-account__hint">
						You have signed up via your Google account. Password management is handled by Google.
					</p>
				</div>
			) : (
				<form className="my-account__field-group" onSubmit={handleUpdatePassword}>
					<h2>Update password</h2>
					<div className="form__group">
						<label htmlFor="current-password-input" className="form__label">Current password</label>
						<input
							id="current-password-input"
							type="password"
							className="form__input"
							placeholder="Current password"
							value={currentPassword}
							onChange={(e) => setCurrentPassword(e.target.value)}
						/>
					</div>
					<div className="form__group">
						<label htmlFor="new-password-input" className="form__label">New password</label>
						<input
							id="new-password-input"
							type="password"
							className="form__input"
							placeholder="New password"
							value={newPassword}
							onChange={(e) => setNewPassword(e.target.value)}
						/>
					</div>
					<div className="form__group">
						<label htmlFor="confirm-password-input" className="form__label">Confirm new password</label>
						<input
							id="confirm-password-input"
							type="password"
							className="form__input"
							placeholder="Confirm new password"
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
						/>
					</div>
					<button
						id="update-password-btn"
						type="submit"
						className="form__button form__button--primary"
						disabled={passwordLoading}
					>
						{passwordLoading ? 'Updating…' : 'Update password'}
					</button>
					{passwordResult && (
						<p className={`my-account__result ${passwordResult.success ? 'my-account__result--success' : 'my-account__result--error'}`}>
							{passwordResult.message}
						</p>
					)}
				</form>
			)}

		</div>
	);
}

/* ------------------------------------------------------------------ */
/*  AppearanceSettings                                                 */
/* ------------------------------------------------------------------ */

const AppearanceSettings = () => {
	const { user, setUser } = useUser()
	const [themeLoading, setThemeLoading] = useState(false)

	const schemes = [
		{ id: 'default', name: 'Default', colours: ['#000', '#fff', '#1a6b1a'] },
		{ id: 'midnight', name: 'Midnight', colours: ['#0f172a', '#e2e8f0', '#6366f1'] },
		{ id: 'ember', name: 'Ember', colours: ['#1c1917', '#fef3c7', '#d97706'] },
	];

	const handleThemeChange = async (themeId) => {
		if (themeId === user?.theme) return;
		setThemeLoading(true)
		try {
			const { data } = await axios.post(
				`${process.env.REACT_APP_WEB_URL}update-theme`,
				{ theme: themeId },
				{ withCredentials: true }
			)
			if (data.success) {
				setUser({ ...user, theme: themeId })
				document.documentElement.setAttribute('data-theme', themeId)
			}
		} catch (error) {
			console.log(error)
		} finally {
			setThemeLoading(false)
		}
	}

	return (
		<div className="my-account__panel">
			<h2>Colour scheme</h2>
			<p className="my-account__hint">Select a colour scheme for the application.</p>
			<div className="my-account__schemes">
				{schemes.map((scheme) => (
					<button
						key={scheme.id}
						id={`scheme-${scheme.id}`}
						className={`my-account__scheme-btn ${user?.theme === scheme.id ? 'my-account__scheme-btn--active' : ''}`}
						onClick={() => handleThemeChange(scheme.id)}
						disabled={themeLoading}
					>
						<span className="my-account__scheme-swatches">
							{scheme.colours.map((colour, i) => (
								<span
									key={i}
									className="my-account__swatch"
									style={{ backgroundColor: colour }}
								/>
							))}
						</span>
						<span className="my-account__scheme-name">{scheme.name}</span>
					</button>
				))}
			</div>
		</div>
	);
}

export default My_Account